import Vue from "vue";
import Vuex from "vuex";
Vue.use(Vuex);

import axios from "axios";

import isUndefined from "lodash/isUndefined";
import without from "lodash/without";
import map from "lodash/map";
import filter from "lodash/filter";
import cloneDeep from "lodash/cloneDeep";
import uniq from "lodash/uniq";
import uniqBy from "lodash/uniqBy";
import each from "lodash/each";

import { returnQueryPath } from "@/store/utils/apiQueryPath";

import { Chord } from "@tonaljs/tonal";
import { Interval } from "@tonaljs/tonal";

import Storage from "store2";

const DEPRECATED_LOCALSTORAGE_CHORDS_LOCATION = "highlight_chords";
const DEPRECATED_LOCALSTORAGE_CHORDS_MIGRATED = "deprecated_chords_migrated";

const LOCALSTORAGE_FAVORITES_SONG_IDS = "favorite_song_ids";
const LOCALSTORAGE_PREFS = "user_prefs";
const LOCALSTORAGE_CHORDS_LIST = "chords_list";

export default new Vuex.Store({
  state: {
    songs: [],
    user: {},
    authorized: false,
    auth_initialized: false,
    chord_list: [
      { name: "C", frets: "0-0-0-3" },
      { name: "G", frets: "0-2-3-2" },
      { name: "F", frets: "2-0-1-0" },
      { name: "Am", frets: "2-0-0-0" },
      { name: "D", frets: "2-2-2-0" },
      { name: "Em", frets: "0-4-3-2" },
      { name: "Dm", frets: "2-2-1-0" },
      { name: "G7", frets: "0-2-1-2" },
      { name: "D7", frets: "2-0-2-0" },
      { name: "A", frets: "2-1-0-0" },
      { name: "Bb", frets: "3-2-1-1" },
      { name: "E7", frets: "1-2-0-2" },
      { name: "C7", frets: "0-0-0-1" },
      { name: "A7", frets: "0-1-0-0" },
    ],
    hero_chords: ["C", "G", "F", "Am", "D", "Em"],
    chordDataLoaded: false,
    onlyKnownChords: false,
    favorite_song_ids: [],
    onlyFavoriteIds: false,
    song_query_results: [],
    song_query_content: "playalong",
    song_query_num_of_pages: 10,
    song_query_page_size: 20,
    song_query_search_query: "",
    song_query_page: 1,
    song_query_source: "all", // all, playalongs, tutorials, favorites
    song_query_mode: "favorites", // all, known-chords
    tuning: "C",
    userPreferences: {
      hideKnownChordShapes: false,
      displayType: "thumb",
      sortOrder: "views-desc",
      knownChordSongCount: 0,
      tuning: "C",
      knownChords: {
        C: ["C", "G", "F"],
        G: ["C", "G", "D"],
      },
      knownCounts: {
        C: 72,
        G: 3,
      },
    },
    username: "",
    toastMessages: [],
    resultsLoading: true,
  },
  getters: {
    base_endpoint: (state, getters) => {
      var base_url = "https://api-py38.ukeability.org/v1";
      // Known chords.
      if (state.song_query_mode == "known-chords") {
        var known_chords_encoded = encodeURIComponent(
          getters.knownChords.join(",")
        );
        return `${base_url}/songs/?user_chord_list=${known_chords_encoded}`;
      }
      // Favorites
      if (state.song_query_mode == "favorites") {
        var favorites = encodeURIComponent(state.favorite_song_ids.join(","));
        return `${base_url}/songs/?favorites=${favorites}`;
      }
      // Normal.
      return `${base_url}/songs/?`;
    },
    active_chord_list: (state, getters) => {
      var tuning = state.tuning || "C";
      if (tuning == "G") {
        return getters.g_chord_list;
      } else {
        return state.chord_list;
      }
    },
    g_chord_list: (state) => {
      var interval = Interval.distance("C4", "G4");
      return map(cloneDeep(state.chord_list), (c) => {
        var chord = Chord.transpose(c.name, interval);
        c.name = chord;
        return c;
      });
    },
    knownChords: (state) => {
      return state.userPreferences.knownChords[state.tuning] || [];
    },
    knownChordSongCount: (state) => {
      return state.userPreferences.knownCounts[state.tuning] || 0;
    },
    ukeTuning: (state) => {
      return state.tuning || "C";
    },
  },
  mutations: {
    set_user(state, user) {
      if (user == null || isUndefined(user)) {
        state.user = {};
        state.authorized = false;
      } else {
        // We have an authenticated user.
        state.user = user;
        state.authorized = true;
        //Sentry.configureScope(scope => {
        //  scope.setUser({
        //    email: state.user.email,
        //    id: state.user.uid
        //  });
        //});
      }
      state.auth_initialized = true;
    },
    set_user_as_authorized(state, username) {
      state.username = username;
      state.authorized = true;
    },
    set_chords_list(state, chord_list) {
      chord_list = uniqBy(chord_list, "name");
      state.chord_list = chord_list;
    },
  },
  actions: {
    updateDisplayType({ state }, value) {
      state.userPreferences.displayType = value;
      Storage.set(LOCALSTORAGE_PREFS, state.userPreferences);
    },
    updateChordHiding({ state }, value) {
      state.userPreferences.hideKnownChordShapes = value;
      Storage.set(LOCALSTORAGE_PREFS, state.userPreferences);
    },
    setTuning({ state }, value) {
      state.tuning = value;
    },
    updateSongQueryModeAndPage({ state, dispatch }, obj) {
      console.log("updateSongQueryModeAndPage", obj);
      state.song_query_source = obj.source;
      state.song_query_mode = obj.mode;
      dispatch("loadSongs", obj.page);
    },
    updateSongQueryMode({ state, dispatch }, mode) {
      console.log(mode);
      state.song_query_mode = mode;
      dispatch("loadSongs", 1);
    },
    updateSortOrder({ state, dispatch }, value) {
      state.userPreferences.sortOrder = value;
      Storage.set(LOCALSTORAGE_PREFS, state.userPreferences);
      dispatch("loadSongs", 1);
    },

    updateSongQueryAndLoad({ state, dispatch }, obj) {
      console.log("updateSongQueryModeAndPage", obj);
      state.onlyFavoriteIds = obj.onlyFavoriteIds;
      state.onlyKnownChords = obj.onlyKnownChords;
      state.song_query_content = obj.content;
      if (obj.searchQuery) {
        state.song_query_search_query = obj.searchQuery;
      } else {
        state.song_query_search_query = "";
      }
      state.song_query_mode = dispatch("loadSongs", obj.page);
    },

    loadSongs({ state, getters }, page) {
      if (!page) {
        page = 1;
      }
      var url = returnQueryPath({
        content: state.song_query_content,
        knownChords: getters.knownChords,
        tuning: getters.ukeTuning,
        onlyKnownChords: state.onlyKnownChords,
        favoriteIds: state.favorite_song_ids,
        onlyFavoriteIds: state.onlyFavoriteIds,
        sortOrder: state.userPreferences.sortOrder,
        searchQuery: state.song_query_search_query,
        pageSize: state.song_query_page_size,
        pageNumber: page,
      });

      console.log(url);
      state.resultsLoading = true;
      axios.get(url).then(function(response) {
        console.log(response.data);
        state.song_query_results = response.data.results;
        state.song_query_num_of_pages =
          Math.ceil(response.data.count / state.song_query_page_size) || 1;
        state.song_query_page = page;
        state.resultsLoading = false;
      });
    },

    updateKnownChordCount({ state, getters }) {
      console.log("updateKnownChordCount - from Django");
      var base_url = "https://api-py38.ukeability.org/v1";
      var known_chords_encoded = encodeURIComponent(
        getters.knownChords.join(",")
      );
      var endpoint = `${base_url}/songs/?tuning=${
        state.tuning
      }&user_chord_list=${known_chords_encoded}`;
      axios.get(endpoint).then(function(response) {
        console.log(response.data);
        var songCount = response.data.count;
        state.userPreferences.knownCounts[state.tuning] = songCount;
        //state.userPreferences.knownChordSongCount = songCount;
        state.toastMessages.push([]);
        Storage.set(LOCALSTORAGE_PREFS, state.userPreferences);
      });
    },
    loadChordShapesFromDjango({ state, commit }) {
      var base_url = "https://api-py38.ukeability.org/v1";
      var endpoint = `${base_url}/chords/`;
      axios.get(endpoint).then(function(response) {
        console.log(response.data);
        var chord_list = map(response.data, (c) => {
          return {
            name: c.n,
            frets: c.f,
          };
        });
        commit("set_chords_list", chord_list);
        Storage.set(LOCALSTORAGE_CHORDS_LIST, chord_list);
        state.chordDataLoaded = true;
        console.log("Chords data loaded!");
        console.timeEnd("chord from Django");
      });
    },
    toggleKnownChord({ dispatch, state, getters }, chordName) {
      console.log(chordName);
      if (getters.knownChords.includes(chordName)) {
        console.log("remove it");
        state.userPreferences.knownChords[getters.ukeTuning] = without(
          state.userPreferences.knownChords[getters.ukeTuning],
          chordName
        );
      } else {
        console.log("add it");
        state.userPreferences.knownChords[getters.ukeTuning].push(chordName);
      }
      console.log(state.userPreferences.knownChords[getters.ukeTuning]);
      Storage.set(LOCALSTORAGE_PREFS, state.userPreferences);
      dispatch("updateKnownChordCount");
    },
    toggleKnownChordAndUpdateResults({ state, getters, dispatch }, chordName) {
      if (getters.knownChords.includes(chordName)) {
        console.log("remove it");
        state.userPreferences.knownChords[getters.ukeTuning] = without(
          state.userPreferences.knownChords[getters.ukeTuning],
          chordName
        );
      } else {
        var nextToLast = getters.knownChords[getters.knownChords.length - 2];
        console.log(nextToLast);
        state.userPreferences.knownChords[getters.ukeTuning].push(chordName);
      }

      Storage.set(LOCALSTORAGE_PREFS, state.userPreferences);
      dispatch("updateKnownChordCount");
      dispatch("loadSongs", state.song_query_page);
    },

    loadInitialUserData({ state, dispatch, getters, commit }) {
      // Load from LocalStorage.
      var _t = Storage.get("_t", null);
      var _username = Storage.get("username", null);
      if (_t && _username) {
        state.username = _username;
        state.authorized = true;
      }

      state.favorite_song_ids = Storage.get(LOCALSTORAGE_FAVORITES_SONG_IDS, [
        1946,
      ]); // Israel Kamakawiwo'ole is King.

      var _userPreferences = Storage.get(
        LOCALSTORAGE_PREFS,
        state.userPreferences
      );

      // Write the local storage user preferences into the existing state.
      each(_userPreferences, (value, key) => {
        state.userPreferences[key] = value;
      });

      // Does the user have previously saved known chords to migrate.
      var _previously_saved_chords = Storage.get(
        DEPRECATED_LOCALSTORAGE_CHORDS_LOCATION,
        []
      );
      var chordsMigrated = Storage.get(
        DEPRECATED_LOCALSTORAGE_CHORDS_MIGRATED,
        false
      );

      _previously_saved_chords = uniq(_previously_saved_chords);

      if (_previously_saved_chords.length > 0 && !chordsMigrated) {
        // We have previous chords. Migrate these.
        console.warn("We have previous known chords that need migrating.");
        state.userPreferences.knownChords = {
          C: _previously_saved_chords,
          G: ["C", "G", "D"],
        };
        Storage.set(DEPRECATED_LOCALSTORAGE_CHORDS_MIGRATED, true);
        Storage.set(LOCALSTORAGE_PREFS, state.userPreferences); // Save it.
      }

      if (state.userPreferences.knownChordSongCount == 0) {
        setTimeout(() => {
          // Delay this call (which pretty much only occurs when new user loads)
          // so that the Lambda isn't hit too many times on initial load.
          dispatch("updateKnownChordCount");
        }, 5000);
      }

      // Set chord_list from Storage.
      var chord_list = Storage.get(LOCALSTORAGE_CHORDS_LIST, []);
      commit("set_chords_list", chord_list);
      if (state.chord_list.length > 0) {
        console.log("Chords data loaded!");
        state.chordDataLoaded = true;
        console.timeEnd("chord from storage");

        // Update hero chords so that 2 known and 4 unknown are showing on first load.
        var unknownChords = filter(state.chord_list, (c) => {
          var _name = c.name;
          return !getters.knownChords.includes(_name);
        });
        state.hero_chords = map(unknownChords.slice(0, 4), "name");
        state.hero_chords = getters.knownChords
          .slice(getters.knownChords.length - 2)
          .concat(state.hero_chords);
        // End hero chord update.
        setTimeout(() => {
          // Delay this call (which needs to happen at some point during the session
          // in the rare instance that a new chord shape has been added)
          // so that the Lambda isn't hit too many times on initial load.
          dispatch("loadChordShapesFromDjango");
        }, 10000);
      } else {
        // Load in the chord shapes immediately.
        dispatch("loadChordShapesFromDjango");
      }
    },
    clearLocalStorage() {
      console.log("clearLocalStorage");
      Storage.clear();
    },
    addSongSubmission({ state }, obj) {
      console.log(state.user.uid);

      // Post to API.
      var url = "https://api-py38.ukeability.org/v1/submissions/";
      //url = "http://127.0.0.1:8000/v1/submissions/";
      return axios
        .post(url, {
          title: obj.title,
          chord_array: obj.chords,
          youtube_id: obj.youtube_id,
          key: obj.key,
          email: obj.email,
          primary_tuning: obj.primary_tuning,
          content: obj.content,
          video_accessible: obj.videoAccessible,
          note: obj.note,
        })
        .then(function(response) {
          console.log(response);
        })
        .catch(function(error) {
          console.log(error);
        });
    },
    toggleFavorite({ state }, song) {
      console.log("toggleFavorite", song);
      var songId = song.id;
      if (state.favorite_song_ids.includes(songId)) {
        state.favorite_song_ids = without(state.favorite_song_ids, songId);
      } else {
        state.favorite_song_ids.push(songId);
      }
      console.log(state.favorite_song_ids);
      Storage.set(LOCALSTORAGE_FAVORITES_SONG_IDS, state.favorite_song_ids);
    },
    logout({ state }) {
      console.log("logout");
      state.authorized = false;
      state.username = null;
      Storage.remove("_t");
    },
  },
});
